import { useId, useState, type ReactNode } from 'react';
import {
  MAX_PERIOD_DAYS,
  isDay,
  periodLabel,
  periodPresets,
  presetWord,
  validSpan,
  type Period,
  type PeriodChoice,
  type PeriodPreset,
} from './period';
import '../insights/insights.css';
import './period.css';

export interface PeriodPickerProps {
  value: PeriodChoice;
  /** The days `value` means today: what the words beside the control say. */
  period: Period;
  onChange: (choice: PeriodChoice) => void;
  /** The UTC day it is, yyyy-MM-dd: the last day a custom span may end on. */
  today: string;
}

const ALL: readonly PeriodPreset[] = [...periodPresets, 'custom'];

/** Why a pair of days cannot be asked for, in a few words; null when it can. */
function trouble(from: string, until: string): string | null {
  if (!isDay(from) || !isDay(until)) return 'Pick both days';
  if (until < from) return 'The last day is before the first';
  return validSpan(from, until) ? null : `At most ${MAX_PERIOD_DAYS} days`;
}

/** One day of the span: a labelled date input. */
function DayField(props: {
  label: string;
  value: string;
  min?: string | undefined;
  max: string;
  invalid: boolean;
  onChange: (day: string) => void;
}): ReactNode {
  const id = useId();
  return (
    <>
      <label htmlFor={id}>{props.label}</label>
      <input
        id={id}
        type="date"
        value={props.value}
        min={props.min}
        max={props.max}
        aria-invalid={props.invalid}
        onChange={(e) => props.onChange(e.currentTarget.value)}
      />
    </>
  );
}

/** The days being typed: the period's own until one is edited, and again whenever the period changes from outside. */
function useDraft(period: Period): [Period, (next: Period) => void] {
  const [draft, setDraft] = useState(period);
  const [seen, setSeen] = useState(period);
  // A preset or a pasted link changed the period: the fields follow it.
  if (seen.from !== period.from || seen.until !== period.until) {
    setSeen(period);
    setDraft(period);
  }
  return [draft, setDraft];
}

/** The two days of a custom span. A valid pair applies as soon as it is one; a bad pair says why. */
function CustomDates(props: {
  period: Period;
  today: string;
  onChange: PeriodPickerProps['onChange'];
}): ReactNode {
  const { today, onChange } = props;
  const [draft, setDraft] = useDraft(props.period);
  const wrong = trouble(draft.from, draft.until);
  const edit = (next: Period) => {
    setDraft(next);
    if (trouble(next.from, next.until) === null) onChange({ preset: 'custom', ...next });
  };
  const bad = wrong !== null;
  return (
    <span className="ad-period__dates">
      <DayField
        label="From"
        value={draft.from}
        max={draft.until || today}
        invalid={bad}
        onChange={(from) => edit({ ...draft, from })}
      />
      <DayField
        label="Until"
        value={draft.until}
        min={draft.from}
        max={today}
        invalid={bad}
        onChange={(until) => edit({ from: draft.from, until })}
      />
      {wrong ? (
        <span className="ad-period__wrong" role="alert">
          {wrong}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The admin area's one period control: five presets and Custom as a segmented switch (a select on a
 * phone, where six buttons do not fit), the two days of a custom span, and the resolved days in words.
 */
export function PeriodPicker({ value, period, onChange, today }: PeriodPickerProps): ReactNode {
  const pick = (preset: PeriodPreset) =>
    onChange(preset === 'custom' ? { preset, from: period.from, until: period.until } : { preset });
  return (
    <div className="ad-period" role="group" aria-label="Period">
      <div className="ad-switch ad-period__switch">
        {ALL.map((preset) => (
          <button
            key={preset}
            type="button"
            aria-pressed={value.preset === preset}
            onClick={() => pick(preset)}
          >
            {presetWord(preset)}
          </button>
        ))}
      </div>
      <select
        className="ad-period__select"
        aria-label="Period preset"
        value={value.preset}
        onChange={(e) => pick(e.currentTarget.value as PeriodPreset)}
      >
        {ALL.map((preset) => (
          <option key={preset} value={preset}>
            {presetWord(preset)}
          </option>
        ))}
      </select>
      {value.preset === 'custom' ? (
        <CustomDates period={period} today={today} onChange={onChange} />
      ) : null}
      <span className="ad-period__words">{periodLabel(period, undefined, today)} · UTC days</span>
    </div>
  );
}
